import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Paper,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  Divider,
  Card,
  CardContent,
  Autocomplete,
  Chip,
  Snackbar,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  SaveOutlined,
  ArrowBackIosNewOutlined,
  SwapHorizOutlined,
  SearchOutlined,
  LinkOutlined,
  EventOutlined,
  WarningAmberOutlined,
  CheckCircleOutlined,
  HourglassEmptyOutlined,
  CancelOutlined,
  ArrowForwardOutlined,
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { tokens } from '../../theme';
import { getToken, base_url } from '../../config/config';
import axios from 'axios';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

const StockMovementForm = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEditMode = !!id;
  
  const initialProductId = location.state?.productId || '';
  const initialAnrId = location.state?.anrId || '';
  
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showAnrDialog, setShowAnrDialog] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  
  const [moveData, setMoveData] = useState({
    moveId: `MOVE-${uuidv4().substring(0, 8)}`,
    productId: initialProductId,
    quantity: 1,
    fromLocation: 'stock',
    toLocation: 'production',
    anrId: initialAnrId,
    state: 'draft',
    moveDate: new Date(),
  });

  // Locations for dropdowns
  const locations = [
    { value: 'supplier', label: 'Supplier' },
    { value: 'production', label: 'Production' },
    { value: 'quality', label: 'Quality Control' },
    { value: 'stock', label: 'Warehouse' },
    { value: 'customer', label: 'Customer' },
  ];

  // Status options
  const statusOptions = [
    { value: 'draft', label: 'Draft', icon: <HourglassEmptyOutlined />, color: colors.grey[500] },
    { value: 'assigned', label: 'Assigned', icon: <EventOutlined />, color: colors.blueAccent[500] },
    { value: 'done', label: 'Done', icon: <CheckCircleOutlined />, color: colors.greenAccent[500] },
    { value: 'cancelled', label: 'Cancelled', icon: <CancelOutlined />, color: colors.redAccent[500] },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = getToken();
        
        // Fetch all products
        const productsResponse = await axios.get(`${base_url}/v1/stock/products`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (productsResponse.data.success) {
          setProducts(productsResponse.data.data);
          
          // If initialProductId is provided, find and set the selected product
          if (initialProductId) {
            const product = productsResponse.data.data.find(p => p._id === initialProductId);
            if (product) {
              setSelectedProduct(product);
            }
          }
        }
        
        // Fetch projects/ANRs
        const projectsResponse = await axios.get(`${base_url}/v1/anrlist`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (projectsResponse.data.data) {
          setProjects(projectsResponse.data.data);
        }
        
        // If in edit mode, fetch movement data
        if (isEditMode) {
          const moveResponse = await axios.get(`${base_url}/v1/stock/moves/${id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (moveResponse.data.success) {
            const move = moveResponse.data.data;
            setMoveData({
              moveId: move.moveId,
              productId: move.productId._id,
              quantity: move.quantity,
              fromLocation: move.fromLocation,
              toLocation: move.toLocation,
              anrId: move.anrId || '',
              state: move.state,
              moveDate: new Date(move.moveDate),
            });
            
            // Set selected product
            setSelectedProduct(move.productId);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setAlert({
          open: true,
          message: 'Failed to load data',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id, isEditMode, initialProductId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'quantity') {
      setMoveData({
        ...moveData,
        [name]: parseFloat(value) || 0
      });
    } else {
      setMoveData({
        ...moveData,
        [name]: value
      });
    }
  };

  const handleDateChange = (newDate) => {
    setMoveData({
      ...moveData,
      moveDate: newDate
    });
  };

  const handleProductSelect = (event, product) => {
    setSelectedProduct(product);
    if (product) {
      setMoveData({
        ...moveData,
        productId: product._id
      });
    } else {
      setMoveData({
        ...moveData,
        productId: ''
      });
    }
  };

  const handleAnrSelect = (anrId) => {
    setMoveData({
      ...moveData,
      anrId
    });
    setShowAnrDialog(false);
  };

  const handleSaveMovement = async () => {
    // Validate required fields
    if (!moveData.productId || moveData.quantity <= 0) {
      setAlert({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error'
      });
      return;
    }
    
    // If source and destination are the same, show error
    if (moveData.fromLocation === moveData.toLocation) {
      setAlert({
        open: true,
        message: 'Source and destination locations cannot be the same',
        severity: 'error'
      });
      return;
    }
    
    // If it's a stock reduction, check if there's enough stock
    if (moveData.fromLocation === 'stock' && moveData.state === 'done') {
      if (selectedProduct && selectedProduct.stockQuantity < moveData.quantity) {
        setAlert({
          open: true,
          message: `Not enough stock. Available: ${selectedProduct.stockQuantity} ${selectedProduct.unitName}`,
          severity: 'error'
        });
        return;
      }
    }
    
    setSaving(true);
    try {
      const token = getToken();
      
      let response;
      if (isEditMode) {
        // Update existing movement
        response = await axios.put(
          `${base_url}/v1/stock/moves/${id}`,
          moveData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } else {
        // Create new movement
        response = await axios.post(
          `${base_url}/v1/stock/moves`,
          moveData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }
      
      if (response.data.success) {
        setAlert({
          open: true,
          message: `Stock movement ${isEditMode ? 'updated' : 'created'} successfully`,
          severity: 'success'
        });
        
        // Navigate back after delay
        setTimeout(() => {
          if (moveData.anrId) {
            navigate(`/stock/anr/${moveData.anrId}`);
          } else {
            navigate('/stock/movements');
          }
        }, 1500);
      } else {
        setAlert({
          open: true,
          message: response.data.message || 'Operation failed',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error saving stock movement:', error);
      setAlert({
        open: true,
        message: error.response?.data?.message || 'Failed to save stock movement',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px">
      {/* HEADER */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h2" color={colors.grey[100]} fontWeight="bold" mb="5px">
            {isEditMode ? 'Edit Stock Movement' : 'Create Stock Movement'}
          </Typography>
          <Typography variant="h5" color={colors.greenAccent[400]}>
            {isEditMode ? 'Update movement details' : 'Record a new stock movement'}
          </Typography>
        </Box>
        
        <Box>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ArrowBackIosNewOutlined />}
            onClick={() => navigate(-1)}
            sx={{ mr: 1 }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveOutlined />}
            onClick={handleSaveMovement}
            disabled={saving}
          >
            {isEditMode ? 'Update Movement' : 'Create Movement'}
          </Button>
        </Box>
      </Box>

      {/* MOVEMENT FORM */}
      <Paper sx={{ p: 3, bgcolor: colors.primary[400], mt: 2 }}>
        <Grid container spacing={3}>
          {/* MAIN DETAILS */}
          <Grid item xs={12} md={8}>
            <Typography variant="h4" mb={2}>Movement Details</Typography>
            
            <TextField
              label="Movement ID"
              name="moveId"
              value={moveData.moveId}
              onChange={handleInputChange}
              fullWidth
              required
              variant="filled"
              disabled={isEditMode}
              sx={{ mb: 2 }}
            />
            
            <Autocomplete
              options={products}
              getOptionLabel={(option) => `${option.name} (${option.artikelNummer})`}
              value={selectedProduct}
              onChange={handleProductSelect}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Product"
                  variant="filled"
                  required
                  error={!moveData.productId}
                  helperText={!moveData.productId ? 'Product is required' : ''}
                  sx={{ mb: 2 }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <SearchOutlined sx={{ color: colors.grey[500], mr: 1 }} />
                        {params.InputProps.startAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Quantity"
                  name="quantity"
                  type="number"
                  value={moveData.quantity}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  variant="filled"
                  InputProps={{
                    inputProps: { min: 0.01, step: 0.01 },
                    endAdornment: selectedProduct && (
                      <Typography variant="body2" color={colors.grey[400]}>
                        {selectedProduct.unitName}
                      </Typography>
                    )
                  }}
                  sx={{ mb: 2 }}
                  error={moveData.quantity <= 0}
                  helperText={moveData.quantity <= 0 ? 'Quantity must be greater than 0' : ''}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    label="Movement Date & Time"
                    value={moveData.moveDate}
                    onChange={handleDateChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        variant="filled"
                        sx={{ mb: 2 }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
            
            <FormControl fullWidth variant="filled" sx={{ mb: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                name="state"
                value={moveData.state}
                onChange={handleInputChange}
                required
              >
                {statusOptions.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    <Box display="flex" alignItems="center">
                      <Box 
                        component="span" 
                        sx={{ 
                          color: status.color,
                          display: 'flex',
                          alignItems: 'center',
                          mr: 1
                        }}
                      >
                        {status.icon}
                      </Box>
                      {status.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' }, 
                alignItems: 'center',
                gap: 2,
                mb: 2
              }}
            >
              <FormControl 
                variant="filled" 
                sx={{ width: { xs: '100%', sm: '40%' } }}
              >
                <InputLabel>From Location</InputLabel>
                <Select
                  name="fromLocation"
                  value={moveData.fromLocation}
                  onChange={handleInputChange}
                  required
                >
                  {locations.map((location) => (
                    <MenuItem key={location.value} value={location.value}>
                      {location.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Box 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  width: { xs: '100%', sm: '20%' } 
                }}
              >
                <ArrowForwardOutlined sx={{ fontSize: 30, color: colors.grey[300] }} />
              </Box>
              
              <FormControl 
                variant="filled" 
                sx={{ width: { xs: '100%', sm: '40%' } }}
              >
                <InputLabel>To Location</InputLabel>
                <Select
                  name="toLocation"
                  value={moveData.toLocation}
                  onChange={handleInputChange}
                  required
                >
                  {locations.map((location) => (
                    <MenuItem key={location.value} value={location.value}>
                      {location.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            <Box display="flex" alignItems="center">
              <TextField
                label="Project ANR (Optional)"
                name="anrId"
                value={moveData.anrId}
                onChange={handleInputChange}
                fullWidth
                variant="filled"
                InputProps={{
                  startAdornment: (
                    <LinkOutlined sx={{ color: colors.grey[500], mr: 1 }} />
                  ),
                  readOnly: true
                }}
                sx={{ mb: 2 }}
              />
              <Button 
                variant="contained"
                color="secondary"
                onClick={() => setShowAnrDialog(true)}
                sx={{ ml: 1, mb: 2, height: 56 }}
              >
                Select ANR
              </Button>
            </Box>
          </Grid>
          
          {/* SIDE INFORMATION */}
          <Grid item xs={12} md={4}>
            <Typography variant="h4" mb={2}>Product Information</Typography>
            
            {selectedProduct ? (
              <Card sx={{ bgcolor: colors.primary[500], mb: 3 }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    {selectedProduct.name}
                  </Typography>
                  <Typography variant="body2" color={colors.grey[300]} gutterBottom>
                    {selectedProduct.description || 'No description available'}
                  </Typography>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color={colors.grey[400]}>
                        SKU:
                      </Typography>
                      <Typography variant="body2">
                        {selectedProduct.artikelNummer}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color={colors.grey[400]}>
                        Unit:
                      </Typography>
                      <Typography variant="body2">
                        {selectedProduct.unitName}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color={colors.grey[400]}>
                        Current Stock:
                      </Typography>
                      <Typography 
                        variant="body2"
                        color={selectedProduct.stockQuantity <= selectedProduct.minStockLevel 
                          ? colors.redAccent[400] 
                          : colors.grey[100]
                        }
                        fontWeight={selectedProduct.stockQuantity <= selectedProduct.minStockLevel 
                          ? 'bold' 
                          : 'normal'
                        }
                      >
                        {selectedProduct.stockQuantity} {selectedProduct.unitName}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color={colors.grey[400]}>
                        Min. Level:
                      </Typography>
                      <Typography variant="body2">
                        {selectedProduct.minStockLevel} {selectedProduct.unitName}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color={colors.grey[400]}>
                        Price:
                      </Typography>
                      <Typography variant="body2">
                        €{selectedProduct.purchasePrice.toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color={colors.grey[400]}>
                        Total Value:
                      </Typography>
                      <Typography variant="body2">
                        €{(moveData.quantity * selectedProduct.purchasePrice).toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  {selectedProduct.stockQuantity <= selectedProduct.minStockLevel && (
                    <Box mt={2} display="flex" alignItems="center" gap={1}>
                      <WarningAmberOutlined color="error" />
                      <Typography variant="body2" color={colors.redAccent[400]}>
                        Low stock warning
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                bgcolor={colors.primary[500]}
                height="200px"
                borderRadius={1}
                mb={3}
              >
                <Typography color={colors.grey[400]}>
                  Select a product to see details
                </Typography>
              </Box>
            )}
            
            <Typography variant="h5" mb={2}>Movement Summary</Typography>
            
            <Box bgcolor={colors.primary[500]} p={2} borderRadius={1}>
              <Typography variant="body2" color={colors.grey[300]} gutterBottom>
                <strong>Movement Type:</strong> {moveData.fromLocation} → {moveData.toLocation}
              </Typography>
              
              <Typography variant="body2" color={colors.grey[300]} gutterBottom>
                <strong>Status:</strong> {
                  statusOptions.find(s => s.value === moveData.state)?.label || moveData.state
                }
              </Typography>
              
              {moveData.anrId && (
                <Typography variant="body2" color={colors.grey[300]} gutterBottom>
                  <strong>ANR:</strong> {moveData.anrId}
                </Typography>
              )}
              
              <Box mt={2}>
                <Chip 
                  icon={getMovementTypeIcon(moveData.fromLocation, moveData.toLocation)}
                  label={getMovementTypeLabel(moveData.fromLocation, moveData.toLocation)}
                  color={moveData.fromLocation === 'stock' ? 'error' : 'success'}
                  variant="outlined"
                />
              </Box>
              
              {moveData.state === 'done' && moveData.fromLocation === 'stock' && (
                <Alert 
                  severity="info" 
                  icon={<InfoIcon />} 
                  variant="outlined"
                  sx={{ mt: 2 }}
                >
                  This movement will reduce available stock by {moveData.quantity} {selectedProduct?.unitName || 'units'}.
                </Alert>
              )}
              
              {moveData.state === 'done' && moveData.toLocation === 'stock' && (
                <Alert 
                  severity="info" 
                  icon={<InfoIcon />} 
                  variant="outlined"
                  sx={{ mt: 2 }}
                >
                  This movement will increase available stock by {moveData.quantity} {selectedProduct?.unitName || 'units'}.
                </Alert>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* ANR SELECTION DIALOG */}
      <Dialog 
        open={showAnrDialog}
        onClose={() => setShowAnrDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Select Project ANR</DialogTitle>
        <DialogContent dividers>
          {projects.length > 0 ? (
            <Grid container spacing={1}>
              {projects.map((project) => (
                <Grid item xs={12} sm={6} md={4} key={project.ANR}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: colors.primary[500],
                        transform: 'translateY(-2px)',
                        transition: 'all 0.3s'
                      },
                      backgroundColor: moveData.anrId === project.ANR 
                        ? colors.greenAccent[700] 
                        : colors.primary[400]
                    }}
                    onClick={() => handleAnrSelect(project.ANR)}
                  >
                    <CardContent>
                      <Typography variant="h5" gutterBottom>
                        {project.ANR}
                      </Typography>
                      <Typography variant="body2" color={colors.grey[300]}>
                        {project.Auftragsbezeichnung || 'No description'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box display="flex" justifyContent="center" p={3}>
              <Typography>No projects found</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleAnrSelect('')} color="error">
            Clear Selection
          </Button>
          <Button onClick={() => setShowAnrDialog(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* ALERT SNACKBAR */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Helper components and functions
const InfoIcon = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  return (
    <SwapHorizOutlined sx={{ color: colors.blueAccent[500] }} />
  );
};

const getMovementTypeIcon = (from, to) => {
  if (from === 'stock') {
    return <ArrowForwardOutlined />;
  } else if (to === 'stock') {
    return <SwapHorizOutlined />;
  } else {
    return <SwapHorizOutlined />;
  }
};

const getMovementTypeLabel = (from, to) => {
  if (from === 'stock' && to === 'production') {
    return 'Stock to Production';
  } else if (from === 'production' && to === 'stock') {
    return 'Production to Stock';
  } else if (from === 'supplier' && to === 'stock') {
    return 'Goods Receipt';
  } else if (from === 'stock' && to === 'customer') {
    return 'Goods Issue';
  } else {
    return `${from} to ${to}`;
  }
};

export default StockMovementForm;